import { useState } from "react";
import { useResetPassword } from "ra-supabase-core";
import { Form, required, useNotify, useRedirect, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AuthLayout } from "@/components/atomic-crm/login/AuthLayout";
import {
  authSubmitButtonClassName,
  glassFormClassName,
  glassInputClassName,
} from "@/components/atomic-crm/login/authStyles";

interface FormData {
  email: string;
}

export const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);

  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [, { mutateAsync: resetPassword }] = useResetPassword({
    onSuccess: () => {
      redirect("/login?passwordRecoveryEmailSent=1");
    },
    onError: () => undefined,
  });

  const submit = async (values: FormData) => {
    try {
      setLoading(true);
      await resetPassword({
        email: values.email,
      });
    } catch (error: any) {
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
            ? "ra.auth.sign_in_error"
            : error.message,
        {
          type: "warning",
          messageArgs: {
            _:
              typeof error === "string"
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={translate("ra-supabase.reset_password.forgot_password", {
        _: "Forgot password?",
      })}
      subtitle={translate(
        "ra-supabase.reset_password.forgot_password_details",
        {
          _: "Enter your email to receive a reset password link.",
        },
      )}
    >
      <Form<FormData>
        className={cn("space-y-6", glassFormClassName)}
        onSubmit={submit as SubmitHandler<FieldValues>}
      >
        <TextInput
          source="email"
          label={translate("ra.auth.email", {
            _: "Email",
          })}
          autoComplete="email"
          validate={required()}
          inputClassName={glassInputClassName}
        />
        <Button
          type="submit"
          className={authSubmitButtonClassName}
          disabled={loading}
        >
          {translate("crm.action.reset_password", {
            _: "Reset password",
          })}
        </Button>
      </Form>
    </AuthLayout>
  );
};

ForgotPasswordPage.path = "forgot-password";
